import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Grow,
    Typography,
    Box,
    LinearProgress,
    TablePagination,
    TextField,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { fetchCategorias } from '../../services/empleados.api';
import { useEffect, useState } from 'react';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: 'rgba(21, 101, 192, 0.02)',
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 500,
    padding: '12px 16px',
    borderBottom: '1px solid rgba(0,0,0,0.05)'
}));

const TareasTable = ({ 
    data, 
    loading, 
    onEdit, 
    onDelete,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    filters,
    onFilterChange
}) => {
    const [categorias, setCategorias] = useState([]);

    useEffect(() => {
        fetchCategorias().then(data => {
            setCategorias(Array.isArray(data) ? data : (data.data || []));
        });
    }, []);

    const getCategoriaName = (id) => categorias.find(c => c.id_categoria === id)?.nombre || id;

    if (loading) {
        return (
            <Box sx={{ width: '100%', p: 4 }}>
                <LinearProgress color="primary" sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2, fontWeight: 600 }}>
                    Cargando tareas...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <TableContainer 
                component={Paper} 
                elevation={0}
                sx={{
                    borderRadius: 4,
                    border: "1px solid rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
                    bgcolor: 'background.paper'
                }}
            >
                <Table sx={{ minWidth: 650 }} aria-label="tabla de tareas">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ bgcolor: '#f8fbfc', fontWeight: 'bold', color: 'text.primary', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '0.875rem' }}>Nombre</TableCell>
                            <TableCell sx={{ bgcolor: '#f8fbfc', fontWeight: 'bold', color: 'text.primary', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '0.875rem' }}>Categoría</TableCell>
                            <TableCell align="right" sx={{ bgcolor: '#f8fbfc', fontWeight: 'bold', color: 'text.primary', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '0.875rem' }}>Acciones</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#f8fbfc' }}>
                            <TableCell sx={{ py: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Filtrar nombre..."
                                    value={filters?.nombre || ''}
                                    onChange={(e) => onFilterChange('nombre', e.target.value)}
                                    variant="outlined"
                                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                                />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={filters?.categoria_id || ''}
                                        onChange={(e) => onFilterChange('categoria_id', e.target.value)}
                                        displayEmpty
                                        variant="outlined"
                                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                                    >
                                        <MenuItem value=""><em>Todas</em></MenuItem>
                                        {categorias.map((cat) => (
                                            <MenuItem key={cat.id_categoria} value={cat.id_categoria}>{cat.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((row, index) => (
                            <Grow
                                in={true}
                                key={row.id_tarea || row.id || index}
                                timeout={300 + (index * 50)}
                            >
                                <StyledTableRow>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                                            {row.nombre}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ fontSize: '0.85rem' }}>{getCategoriaName(row.categoria_id)}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Tooltip title="Editar">
                                            <IconButton onClick={() => onEdit(row)} color="primary" sx={{ 
                                                bgcolor: 'primary.light', 
                                                mr: 1,
                                                '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                            }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton onClick={() => onDelete(row)} color="error" sx={{ 
                                                bgcolor: 'error.light',
                                                '&:hover': { bgcolor: 'error.main', color: 'white' }
                                            }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </StyledTableRow>
                            </Grow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                labelRowsPerPage="Filas por página"
                sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}
            />
        </Box>
    );
};

export default TareasTable;
